import { forwardRef } from "react";
import type { SelectHTMLAttributes } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, placeholder, className = "", id, ...rest }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-stone-700 mb-1.5">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={inputId}
            className={`
              block w-full appearance-none rounded-lg border text-sm
              pl-3 pr-9 py-2.5 transition-colors
              ${
                error
                  ? "border-red-400 bg-red-50 focus:ring-red-400"
                  : "border-stone-300 bg-white focus:ring-forest-500"
              }
              focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent
              disabled:bg-stone-50 disabled:text-stone-400 disabled:cursor-not-allowed
              ${className}
            `}
            {...rest}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-stone-400">
            <ChevronDownIcon className="w-4 h-4" />
          </div>
        </div>
        {hint && !error && <p className="mt-1.5 text-xs text-stone-400">{hint}</p>}
        {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
      </div>
    );
  }
);

Select.displayName = "Select";
